import React from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import { triggerSameTabDownload } from 'scenes/ProjectBank/utils/projectBankDownload';
import { selectProjectBankArchiveJobById } from 'store/projectBankArchiveJobs/selectors/projectBankArchiveJobs.selectors';
import { formatArchiveSize } from 'store/projectBankArchiveJobs/utils/formatArchiveSize';

// The app-wide snackbars of an in-browser archive download. They are
// enqueued through notistack's module-level api from the job thunks, so they
// need no mounted component of their own and outlive the scene that started
// the job; the SnackbarProvider sits inside the Redux Provider, which is what
// lets the progress content read the job from the store. A job whose link
// goes out as a project email shows none of these.

const progressSnackbarKey = (archiveJobId) =>
  `project-bank-archive-job-${archiveJobId}`;

/**
 * Live progress of one job: files done while the worker builds, parts copied
 * while it assembles a parallel build, and the exact size once the worker
 * has resolved the entries.
 */
export const ArchiveJobProgressContent = ({ archiveJobId }) => {
  const { t } = useTranslation('common');
  const job = useSelector((state) =>
    selectProjectBankArchiveJobById(state, archiveJobId)
  );
  const assembling = job?.stage === 'ASSEMBLING';
  const label = assembling
    ? t('bank.assembling_archive')
    : t('bank.preparing_archive');
  const counter = assembling
    ? job.totalParts > 0
      ? ` ${job.doneParts}/${job.totalParts}`
      : ''
    : job?.totalFiles > 0
      ? ` ${job.doneFiles}/${job.totalFiles}`
      : '';
  const sizeText =
    job?.totalBytes > 0 ? ` (${formatArchiveSize(job.totalBytes)})` : '';

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <span>
        {label}...{counter}
        {sizeText}
      </span>
      <CircularProgress size={20} style={{ color: 'white' }} />
    </div>
  );
};

/** @returns {string} the snackbar key, for closeSnackbar */
export const showArchiveJobProgressSnackbar = (archiveJobId) => {
  const key = progressSnackbarKey(archiveJobId);
  enqueueSnackbar(<ArchiveJobProgressContent archiveJobId={archiveJobId} />, {
    key,
    variant: 'info',
    persist: true,
    preventDuplicate: true,
  });
  return key;
};

const ReadyAction = ({ snackbarKey, downloadUrl }) => {
  const { t } = useTranslation('common');
  return (
    <Button
      color="inherit"
      size="small"
      onClick={() => {
        triggerSameTabDownload(downloadUrl);
        closeSnackbar(snackbarKey);
      }}
    >
      {t('common.download')}
    </Button>
  );
};

/**
 * A finished job found on app start: the download is offered with a button
 * (a user gesture) rather than started on its own the moment the app opens.
 * @param {object} result the DONE poll response
 */
export const showArchiveReadySnackbar = (result) => {
  enqueueSnackbar(`${result.fileName} — ${i18n.t('bank.archive_ready')}`, {
    key: `project-bank-archive-ready-${result.downloadUrl}`,
    variant: 'success',
    persist: true,
    preventDuplicate: true,
    action: (key) => (
      <ReadyAction snackbarKey={key} downloadUrl={result.downloadUrl} />
    ),
  });
};
